// ============================================
// JOBS LIST PAGE
// ============================================
// TIER-4 UPDATE:
// [ŞİRKET-H8] Department filtresi tip düzeltmesi
// API client güncellendikten sonra `as any` kaldırılabilir
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Plus,
  MoreVertical,
  Eye,
  Edit,
  Pause,
  Play,
  Trash2,
  Briefcase,
  DollarSign,
  Users,
  Calendar,
  XCircle,
  Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  PageHeader,
  DataTable,
  StatusBadge,
  FilterBar,
  FilterSelect,
  ConfirmDialog,
} from '@/components';
import { jobsApi } from '@/services/api';
import { usePagination, useDebounce, useDeleteJob, useMyCompany } from '@/hooks';
import { formatDate, formatCurrency } from '@/utils';
import type { Column } from '@/components';

export const Route = createFileRoute('/_authenticated/jobs/')({
  component: JobsListPage,
});

const departmentLabels: Record<string, string> = {
  DECK: 'Güverte',
  ENGINE: 'Makine',
  ELECTRICAL: 'Elektrik',
  CATERING: 'Catering',
  OTHER: 'Diğer',
};

const statusOptions = [
  { value: 'ACTIVE', label: 'Aktif' },
  { value: 'DRAFT', label: 'Taslak' },
  { value: 'PAUSED', label: 'Duraklatıldı' },
  { value: 'CLOSED', label: 'Kapatıldı' },
  { value: 'EXPIRED', label: 'Süresi Doldu' },
];

const departmentOptions = [
  { value: 'DECK', label: 'Güverte' },
  { value: 'ENGINE', label: 'Makine' },
  { value: 'ELECTRICAL', label: 'Elektrik' },
  { value: 'CATERING', label: 'Catering' },
  { value: 'OTHER', label: 'Diğer' },
];

function JobsListPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const { page, limit, goToPage, setLimit } = usePagination(1, 10);
  const debouncedSearch = useDebounce(search, 300);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    job: any | null;
    action: 'pause' | 'activate' | 'close' | 'delete' | 'dope';
  }>({ open: false, job: null, action: 'pause' });

  const deleteJobMutation = useDeleteJob();
  const { data: companyResponse } = useMyCompany();
  const company = (companyResponse as any)?.data || companyResponse;

  const dopeJobMutation = useMutation({
    mutationFn: (id: string) => jobsApi.dope(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['company', 'me'] });
      setConfirmDialog({ open: false, job: null, action: 'pause' });
      toast.success('İlan başarıyla öne çıkarıldı!');
    },
    onError: (err: any) => {
      const errMsg = err?.response?.data?.message || err?.message || 'Bir hata oluştu';
      toast.error(errMsg);
    },
  });

  // [ŞİRKET-H8] Fetch jobs with department filter - using direct API call with auth store
  const { data, isLoading } = useQuery({
    queryKey: ['jobs', 'me', { page, limit, search: debouncedSearch, status: statusFilter, department: departmentFilter }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(limit));
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (statusFilter) params.append('status', statusFilter);
      if (departmentFilter) params.append('department', departmentFilter);
      
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
      
      // Get token from persisted auth storage
      const authStorage = localStorage.getItem('company-auth-storage');
      let token = '';
      if (authStorage) {
        try {
          const parsed = JSON.parse(authStorage);
          token = parsed.state?.accessToken || '';
        } catch (e) {
          console.error('Failed to parse auth storage:', e);
        }
      }
      
      const response = await fetch(`${baseUrl}/jobs/me?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }
      
      return response.json();
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      jobsApi.updateStatus(id, { status: status as any }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', 'me'] });
      setConfirmDialog({ open: false, job: null, action: 'pause' });
    },
  });

  const handleConfirmAction = () => {
    if (!confirmDialog.job) return;
    
    if (confirmDialog.action === 'delete') {
      deleteJobMutation.mutate(confirmDialog.job.id, {
        onSuccess: () => {
          setConfirmDialog({ open: false, job: null, action: 'pause' });
        },
      });
      return;
    }

    if (confirmDialog.action === 'dope') {
      dopeJobMutation.mutate(confirmDialog.job.id);
      return;
    }
    
    const statusMap: Record<string, string> = {
      pause: 'PAUSED',
      activate: 'ACTIVE',
      close: 'CLOSED',
    };
    updateStatusMutation.mutate({
      id: confirmDialog.job.id,
      status: statusMap[confirmDialog.action],
    });
  };

  const columns: Column<any>[] = [
    {
      key: 'job',
      header: 'İlan',
      render: (job) => (
        <div>
          <div className="flex items-center gap-2">
            <Link
              to="/jobs/$slug"
              params={{ slug: job.slug }}
              className="font-medium text-gray-900 hover:text-maritime-600"
            >
              {job.title}
            </Link>
            {job.isUrgent && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 animate-pulse border border-red-200">
                ACİL
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {departmentLabels[job.department] || job.department}
            {job.rank?.name && ` • ${job.rank.name}`}
          </p>
          {!job.isUrgent && job.status === 'ACTIVE' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDialog({ open: true, job, action: 'dope' });
              }}
              className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-700 hover:text-amber-800 hover:bg-amber-100 bg-amber-50 px-2 py-0.5 rounded-lg border border-amber-200 transition-colors cursor-pointer w-fit"
            >
              <Zap className="w-3 h-3 text-amber-600 fill-amber-500" />
              İlanı dopingleyip başvuru arttırın
            </button>
          )}
        </div>
      ),
    },
    {
      key: 'salary',
      header: 'Maaş',
      render: (job) => (
        <div className="flex items-center gap-1 text-gray-600">
          <DollarSign className="h-4 w-4 text-gray-400" />
          <span className="text-sm">
            {job.salaryMin && job.salaryMax
              ? `${formatCurrency(job.salaryMin)} - ${formatCurrency(job.salaryMax)}`
              : job.salaryMin
                ? `${formatCurrency(job.salaryMin)}+`
                : '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'applications',
      header: 'Başvuru',
      render: (job) => (
        <div className="flex items-center gap-1 text-gray-600">
          <Users className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium">{job._count?.applications || 0}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Durum',
      render: (job) => <StatusBadge status={job.status} dot />,
    },
    {
      key: 'createdAt',
      header: 'Oluşturulma',
      render: (job) => (
        <div className="flex items-center gap-1 text-gray-500">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm">{formatDate(job.createdAt)}</span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (job) => (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="min-w-[160px] bg-white rounded-maritime shadow-lg border border-gray-200 py-1 z-50"
              sideOffset={5}
              align="end"
            >
              <DropdownMenu.Item asChild>
                <Link
                  to="/jobs/$slug"
                  params={{ slug: job.slug }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer outline-none"
                >
                  <Eye className="w-4 h-4" />
                  Görüntüle
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <Link
                  to="/jobs/$id/edit"
                  params={{ id: job.id }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer outline-none"
                >
                  <Edit className="w-4 h-4" />
                  Düzenle
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 border-t border-gray-200" />
              {job.status === 'ACTIVE' ? (
                <>
                  {!job.isUrgent && (
                    <DropdownMenu.Item
                      className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer outline-none font-medium"
                      onSelect={() => setConfirmDialog({ open: true, job, action: 'dope' })}
                    >
                      <Zap className="w-4 h-4 text-red-500 fill-red-500 animate-pulse" />
                      Öne Çıkar (Doping)
                    </DropdownMenu.Item>
                  )}
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 cursor-pointer outline-none"
                    onSelect={() => setConfirmDialog({ open: true, job, action: 'pause' })}
                  >
                    <Pause className="w-4 h-4" />
                    Duraklat
                  </DropdownMenu.Item>
                </>
              ) : job.status === 'PAUSED' || job.status === 'DRAFT' ? (
                <DropdownMenu.Item
                  className="flex items-center gap-2 px-3 py-2 text-sm text-green-600 hover:bg-green-50 cursor-pointer outline-none"
                  onSelect={() => setConfirmDialog({ open: true, job, action: 'activate' })}
                >
                  <Play className="w-4 h-4" />
                  Aktifleştir
                </DropdownMenu.Item>
              ) : null}
              {job.status !== 'CLOSED' && (
                <DropdownMenu.Item
                  className="flex items-center gap-2 px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 cursor-pointer outline-none"
                  onSelect={() => setConfirmDialog({ open: true, job, action: 'close' })}
                >
                  <XCircle className="w-4 h-4" />
                  Kapat
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Separator className="my-1 border-t border-gray-200" />
              <DropdownMenu.Item
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer outline-none"
                onSelect={() => setConfirmDialog({ open: true, job, action: 'delete' })}
              >
                <Trash2 className="w-4 h-4" />
                Kalıcı Olarak Sil
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      ),
    },
  ];

  const activeFiltersCount =
    (statusFilter ? 1 : 0) + (departmentFilter ? 1 : 0) + (search ? 1 : 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="İş İlanları"
        description="Tüm iş ilanlarınızı yönetin"
        actions={
          <Link
            to="/jobs/create"
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Yeni İlan
          </Link>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="İlan başlığı ile ara..."
        filters={
          <>
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              placeholder="Tüm Durumlar"
            />
            <FilterSelect
              value={departmentFilter}
              onChange={setDepartmentFilter}
              options={departmentOptions}
              placeholder="Tüm Departmanlar"
            />
          </>
        }
        activeFiltersCount={activeFiltersCount}
        onClearFilters={() => {
          setSearch('');
          setStatusFilter('');
          setDepartmentFilter('');
        }}
      />

      <DataTable
        columns={columns}
        data={Array.isArray(data?.data?.data) ? data.data.data : (Array.isArray(data?.data) ? data.data : (Array.isArray((data as any)?.items) ? (data as any).items : []))}
        keyExtractor={(job) => job.id}
        loading={isLoading}
        emptyMessage="İlan bulunamadı"
        emptyState={{
          icon: <Briefcase className="w-8 h-8 text-gray-400" />,
          title: 'Henüz ilan yok',
          description: 'İlk iş ilanınızı oluşturarak aday bulmaya başlayın.',
          action: (
            <Link
              to="/jobs/create"
              className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              İlan Oluştur
            </Link>
          ),
        }}
        pagination={
          (data?.data?.meta || data?.meta)
            ? {
                page: data?.data?.meta?.page || data?.meta?.page,
                limit: data?.data?.meta?.limit || data?.meta?.limit,
                total: data?.data?.meta?.total || data?.meta?.total,
                totalPages: data?.data?.meta?.totalPages || data?.meta?.totalPages,
                onPageChange: goToPage,
                onLimitChange: setLimit,
              }
            : undefined
        }
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={
          confirmDialog.action === 'pause'
            ? 'İlanı Duraklat'
            : confirmDialog.action === 'activate'
              ? 'İlanı Aktifleştir'
              : confirmDialog.action === 'delete'
                ? 'İlanı Kalıcı Olarak Sil'
                : confirmDialog.action === 'dope'
                  ? 'İlanı Öne Çıkar (Doping)'
                  : 'İlanı Kapat'
        }
        description={
          confirmDialog.action === 'pause'
            ? `"${confirmDialog.job?.title}" ilanını duraklatmak istediğinize emin misiniz?`
            : confirmDialog.action === 'activate'
              ? `"${confirmDialog.job?.title}" ilanını aktifleştirmek istediğinize emin misiniz?`
              : confirmDialog.action === 'delete'
                ? `"${confirmDialog.job?.title}" ilanını kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz ve tüm başvurular da silinecektir.`
                : confirmDialog.action === 'dope'
                  ? `"${confirmDialog.job?.title}" ilanını öne çıkarmak (24 saatliğine sabitlemek) istediğinize emin misiniz? Bu işlem 1 Acil İlan Kredinizi tüketecektir. Kalan Acil İlan Krediniz: ${company?.urgentJobCredits || 0}`
                  : `"${confirmDialog.job?.title}" ilanını kapatmak istediğinize emin misiniz?`
        }
        variant={
          confirmDialog.action === 'pause'
            ? 'warning'
            : confirmDialog.action === 'activate'
              ? 'success'
              : confirmDialog.action === 'dope'
                ? 'success'
                : 'danger'
        }
        confirmText={
          confirmDialog.action === 'pause'
            ? 'Duraklat'
            : confirmDialog.action === 'activate'
              ? 'Aktifleştir'
              : confirmDialog.action === 'delete'
                ? 'Evet, Kalıcı Olarak Sil'
                : confirmDialog.action === 'dope'
                  ? 'Öne Çıkar (1 Kredi)'
                  : 'Kapat'
        }
        onConfirm={handleConfirmAction}
        loading={updateStatusMutation.isPending || deleteJobMutation.isPending || dopeJobMutation.isPending}
      />
    </div>
  );
}
